import { TrendingDown, TrendingUp } from 'lucide-react';
import {
  getScoreColorVar,
  getScoreLabel,
} from '../../../../helpers/postScoreAndMetrics.helpers';

interface Delta {
  change: number;
  change_direction: 'up' | 'down' | 'unchanged';
}

interface Props {
  score: number;
  replyCount: number;
  delta: Delta | null;
}

const R = 52;
const CIRC = 2 * Math.PI * R;

export default function ScoreHero({ score, replyCount, delta }: Props) {
  const clamped = Math.max(0, Math.min(100, score));
  const offset = CIRC * (1 - clamped / 100);
  const color = getScoreColorVar(clamped);
  const showDelta =
    delta && delta.change_direction !== 'unchanged' && delta.change !== 0;

  // Renders inner content only — SectionShell supplies the `score` anchor +
  // the auto-numbered "Overall score" eyebrow (single source of truth).
  return (
    <div
      className={`POST__ANALYSIS__hero${
        showDelta ? ` flash-${delta!.change_direction}` : ''
      }`}
    >
      <div className="POST__ANALYSIS__hero__gauge">
        <svg
          viewBox="0 0 120 120"
          width="160"
          height="160"
          role="img"
          aria-label={`Score ${Math.round(clamped)} of 100`}
        >
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="var(--border-subtle)"
            strokeWidth="8"
          />
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={offset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
          <text
            x="60"
            y="60"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="30"
            fontWeight="700"
            fill="var(--text-primary)"
          >
            {Math.round(clamped)}
          </text>
          <text
            x="60"
            y="80"
            textAnchor="middle"
            fontSize="9"
            fill="var(--text-muted)"
          >
            / 100
          </text>
        </svg>
      </div>

      <div className="POST__ANALYSIS__hero__meta">
        <h2 className="POST__ANALYSIS__hero__label" style={{ color }}>
          {getScoreLabel(clamped)}
        </h2>
        <div className="POST__ANALYSIS__hero__facts">
          <span>
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'} factored
          </span>
          <span className="POST__ANALYSIS__live">
            <span className="live-dot" /> Live
          </span>
        </div>
        {showDelta && (
          <div
            className={`POST__ANALYSIS__hero__delta delta-${delta!.change_direction}`}
          >
            {delta!.change_direction === 'up' ? (
              <TrendingUp size={16} />
            ) : (
              <TrendingDown size={16} />
            )}
            <span>
              {delta!.change > 0 ? '+' : ''}
              {delta!.change.toFixed(1)}
            </span>{' '}
            pts
          </div>
        )}
      </div>
    </div>
  );
}
